// src/components/auth/registration/BrandsStep.jsx - Updated for existing security rules
import React, { useState, useEffect } from 'react';
import Button from '../../common/Button';
import Input from '../../common/Input';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../../services/firebase';
import { useAuth } from '../../../contexts/AuthContext';

const BrandsStep = ({ data, updateData, nextStep, prevStep }) => {
  const { localBrands, addLocalBrand, removeLocalBrand } = useAuth();
  const [systemBrands, setSystemBrands] = useState([]);
  const [selectedBrands, setSelectedBrands] = useState(data.brands || []);
  const [customBrandInput, setCustomBrandInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Combine system and local brands
  const allBrands = [...systemBrands, ...localBrands];
  
  // Define famous brands in India (both Indian and international brands that are popular in India)
  const famousBrandsInIndia = [
    // Indian brands
    'bajaj', 'hero', 'mahindra', 'tata', 'maruti', 'tvs', 'royal', 'ashok', 'force', 'eicher',
    // International brands famous in India
    'honda', 'yamaha', 'suzuki', 'hyundai', 'toyota', 'ford', 'volkswagen', 'skoda', 'ola', 'triumph',
    'bmw', 'mercedes', 'audi', 'nissan', 'renault', 'kia', 'mg', 'jeep', 'chevrolet',
    'kawasaki', 'ducati', 'ktm', 'benelli', 'aprilia', 'piaggio', 'vespa'
  ];
  
  // Fetch system brands from Firestore (excluding user-created ones)
  useEffect(() => {
    const fetchSystemBrands = async () => {
      try {
        const brandsRef = collection(db, 'brands');
        const snapshot = await getDocs(brandsRef);
        
        const brandsList = snapshot.docs
          .map(doc => {
            const data = doc.data();
            return {
              id: doc.id,
              isGlobal: true,
              isUserCreated: data.isUserCreated || false,
              createdBy: data.createdBy,
              ...data
            };
          })
          // Filter out user-created brands (only show system brands)
          .filter(brand => !brand.isUserCreated);

        setSystemBrands(brandsList);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching system brands:", error);
        setError('Failed to load brands. Using local brands only.');
        setLoading(false);
      }
    };
    
    fetchSystemBrands();
  }, []);
  
  // Add custom brand locally
  const addCustomBrand = async () => {
    if (!customBrandInput.trim()) return;
    
    try {
      // Check if brand already exists
      const existingBrand = allBrands.find(brand =>
        brand.name.toLowerCase() === customBrandInput.trim().toLowerCase()
      );

      if (existingBrand) {
        setError('A brand with this name already exists');
        return;
      }

      const brandData = {
        name: customBrandInput.trim(),
        description: `Custom brand: ${customBrandInput.trim()}`,
        logo: null,
        website: ''
      };
      
      const newBrand = await addLocalBrand(brandData);
      
      // Select the new brand automatically
      setSelectedBrands(prev => [...prev, newBrand.id]);
      setCustomBrandInput('');
      
      setError(`✅ Brand "${brandData.name}" added locally! It will be saved when you complete registration.`);
      setTimeout(() => {
        setError('');
      }, 3000);
      
    } catch (error) {
      console.error('Error adding custom brand:', error);
      setError('Failed to add custom brand. Please try again.');
    }
  };
  
  // Toggle brand selection
  const toggleBrand = (brandId) => {
    setSelectedBrands(prev => {
      if (prev.includes(brandId)) {
        return prev.filter(id => id !== brandId);
      } else {
        return [...prev, brandId];
      }
    });
  };
  
  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (selectedBrands.length === 0) {
      setError('Please select at least one brand');
      return;
    }
    
    const allSelectedBrands = allBrands.filter(brand => selectedBrands.includes(brand.id));
    
    updateData({
      brands: selectedBrands,
      brandObjects: allSelectedBrands
    });
    nextStep();
  };
  
  // Separate brands into Famous (popular in India) and Others
  const famousBrands = systemBrands.filter(brand => {
    const brandName = brand.name?.toLowerCase() || '';
    const brandId = brand.id?.toLowerCase() || '';
    
    // Check if it's marked as famous in Firebase
    if (brand.isFamous === true || brand.category === 'Famous') {
      return true;
    }
    
    // Check if brand name/id contains any famous brand name
    return famousBrandsInIndia.some(famousName => 
      brandName.includes(famousName) || brandId.includes(famousName)
    );
  });
  
  const otherBrands = systemBrands.filter(brand => {
    const brandName = brand.name?.toLowerCase() || '';
    const brandId = brand.id?.toLowerCase() || '';
    
    // Check if it's marked as famous in Firebase
    if (brand.isFamous === true || brand.category === 'Famous') {
      return false;
    }
    
    // Check if brand name/id contains any famous brand name
    const isFamous = famousBrandsInIndia.some(famousName => 
      brandName.includes(famousName) || brandId.includes(famousName)
    );
    
    return !isFamous;
  });
  
  // Enhanced brand card with local brand indicator
  const BrandCard = ({ brand }) => {
    const isSelected = selectedBrands.includes(brand.id);
    const isLocal = brand.isLocal;
    
    return (
      <div 
        className={`relative overflow-hidden rounded-lg border-2 cursor-pointer transition-all duration-200 bg-white ${
          isSelected 
            ? 'border-orange-500 shadow-lg scale-105' 
            : 'border-gray-200 hover:border-orange-300 hover:shadow-md'
        }`}
        onClick={() => toggleBrand(brand.id)}
      >
        {/* Local badge */}
        {isLocal && (
          <div className="absolute top-2 left-2 z-20">
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
              Local
            </span>
          </div>
        )}

        {/* Checkbox */}
        <div className="absolute top-3 right-3 z-10">
          <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
            isSelected 
              ? 'bg-orange-500 border-orange-500' 
              : 'bg-white border-gray-300'
          }`}>
            {isSelected && (
              <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            )}
          </div>
        </div>
        
        {/* Brand Logo */}
        <div className="h-16 w-full bg-gray-50 flex items-center justify-center p-2">
          <img 
            src={brand.logo || `https://via.placeholder.com/100x60/f0f0f0/666666?text=${brand.name?.replace(/\s+/g, '+') || 'Brand'}`} 
            alt={brand.name || 'Brand'}
            className="h-full w-auto max-w-full object-contain"
            onError={(e) => {
              e.target.src = `https://via.placeholder.com/100x60/f0f0f0/666666?text=${brand.name?.replace(/\s+/g, '+') || 'Brand'}`;
            }}
          />
        </div>
        
        {/* Brand Name */}
        <div className="p-3 text-center">
          <h4 className="font-medium text-gray-900 text-sm leading-tight break-words overflow-hidden">
            {brand.name || 'Unknown Brand'}
          </h4>
        </div>
        
        {/* Selection overlay */}
        {isSelected && (
          <div className="absolute inset-0 bg-orange-500 bg-opacity-10 pointer-events-none">
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-orange-500"></div>
          </div>
        )}

        {/* Local brand delete button */}
        {isLocal && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              removeLocalBrand(brand.id);
              setSelectedBrands(prev => prev.filter(id => id !== brand.id));
            }}
            className="absolute top-2 right-8 z-20 p-1 rounded-full bg-red-100 text-red-600 hover:bg-red-200 transition-colors"
            title="Remove local brand"
          >
            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        )}
      </div>
    );
  };
  
  return (
    <form onSubmit={handleSubmit} className="mt-8 space-y-6">
      <div>
        <div className="text-center space-y-4 mb-8">
          <div className="inline-flex items-center space-x-2 bg-orange-100 text-orange-800 px-4 py-2 rounded-full text-sm font-medium">
            <span>🏢</span>
            <span>Vehicle Brands</span>
          </div>
          <h3 className="text-2xl font-bold text-gray-900">
            Select Product Brands
          </h3>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Choose the automobile brands for which you supply spare parts. Select multiple brands to showcase your expertise.
          </p>
        </div>
        
        {loading ? (
          <div className="flex flex-col items-center justify-center py-12 space-y-4">
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-orange-500 border-t-transparent"></div>
            <p className="text-gray-500">Loading brands...</p>
          </div>
        ) : (
          <>
            {error && (
              <div className={`mb-6 p-4 text-sm border rounded-lg flex items-center space-x-2 ${
                error.includes('✅') 
                  ? 'text-green-700 bg-green-50 border-green-200' 
                  : 'text-red-700 bg-red-50 border-red-200'
              }`}>
                <svg className="w-5 h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  {error.includes('✅') ? (
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  ) : (
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  )}
                </svg>
                <span>{error}</span>
              </div>
            )}
            
            {/* Custom brand input */}
            <div className="mb-8 bg-gray-50 rounded-lg p-6">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                🌟 Add Custom Brand
              </label>
              <div className="flex gap-3">
                <Input
                  id="customBrand"
                  name="customBrand"
                  type="text"
                  value={customBrandInput}
                  onChange={(e) => setCustomBrandInput(e.target.value)}
                  placeholder="Enter brand name (e.g., Local Supplier, XYZ Parts)"
                  className="flex-grow"
                  noLabel
                />
                <Button 
                  type="button" 
                  variant="secondary"
                  onClick={addCustomBrand}
                  disabled={!customBrandInput.trim()}
                  className="px-6"
                >
                  Add
                </Button>
              </div>
              <p className="mt-2 text-xs text-gray-500">
                Custom brands will be stored locally and saved to your account when you complete registration.
              </p>
            </div>
            
            {/* Famous Brands Section */}
            {famousBrands.length > 0 && (
              <div className="mb-8">
                <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <span className="text-2xl mr-3">⭐</span>
                  Famous Brands
                  <span className="ml-2 text-sm font-normal text-gray-500">
                    ({famousBrands.length} brands)
                  </span>
                </h4>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                  {famousBrands.map(brand => (
                    <BrandCard key={brand.id} brand={brand} />
                  ))}
                </div>
              </div>
            )}
            
            {/* Other Brands Section */}
            {otherBrands.length > 0 && (
              <div className="mb-8">
                <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <span className="text-2xl mr-3">🔍</span>
                  Other Brands
                  <span className="ml-2 text-sm font-normal text-gray-500">
                    ({otherBrands.length} brands)
                  </span>
                </h4>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                  {otherBrands.map(brand => (
                    <BrandCard key={brand.id} brand={brand} />
                  ))}
                </div>
              </div>
            )}

            {/* Local Brands Section */}
            {localBrands.length > 0 && (
              <div className="mb-8">
                <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <span className="text-2xl mr-3">🏠</span>
                  Your Custom Brands
                  <span className="ml-2 text-sm font-normal text-gray-500">
                    ({localBrands.length} brands)
                  </span>
                </h4>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                  {localBrands.map(brand => (
                    <BrandCard key={brand.id} brand={brand} />
                  ))}
                </div>
              </div>
            )}
            
            {/* If no brands found */}
            {systemBrands.length === 0 && localBrands.length === 0 && (
              <div className="text-center py-12">
                <div className="text-gray-400 text-6xl mb-4">🏢</div>
                <p className="text-gray-500 text-lg">No brands found</p>
                <p className="text-gray-400 text-sm">Add custom brands to get started</p>
              </div>
            )}
            
            {/* Selection Summary */}
            <div className="bg-gray-50 rounded-lg p-6 text-center space-y-2">
              <div className="text-2xl font-bold text-orange-600">
                {selectedBrands.length}
              </div>
              <div className="text-sm text-gray-600">
                brands selected
              </div>
              {selectedBrands.length > 0 && (
                <div className="text-xs text-gray-500 max-w-md mx-auto">
                  Great selection! This shows you work with diverse automotive brands.
                </div>
              )}
            </div>

            {/* Selected Brands Summary */}
            {selectedBrands.length > 0 && (
              <div className="mt-6 p-4 bg-orange-50 border border-orange-200 rounded-xl">
                <h4 className="text-lg font-medium text-orange-900 mb-3">
                  Selected Brands ({selectedBrands.length})
                </h4>
                <div className="flex flex-wrap gap-2">
                  {selectedBrands.map(brandId => {
                    const brand = allBrands.find(b => b.id === brandId);
                    return brand ? (
                      <span
                        key={brandId}
                        className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-orange-100 text-orange-800 border border-orange-200"
                      >
                        {brand.name}
                        {brand.isLocal && (
                          <span className="ml-1 text-purple-600">(Local)</span>
                        )}
                        <button
                          type="button"
                          onClick={() => toggleBrand(brandId)}
                          className="ml-2 inline-flex items-center justify-center w-4 h-4 rounded-full bg-orange-200 hover:bg-orange-300 focus:outline-none"
                        >
                          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                        </button>
                      </span>
                    ) : null;
                  })}
                </div>
              </div>
            )}
          </>
        )}
      </div>
      
      <div className="flex justify-between items-center pt-6 border-t border-gray-200">
        <Button 
          type="button" 
          variant="secondary" 
          onClick={prevStep}
          className="flex items-center space-x-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          <span>Previous</span>
        </Button>
        
        <div className="text-center">
          <p className="text-xs text-gray-500 mb-2">
            {selectedBrands.length === 0 ? 
              'Please select at least one brand' : 
              `${selectedBrands.length} brand${selectedBrands.length !== 1 ? 's' : ''} selected`
            }
          </p>
        </div>
        
        <Button 
          type="submit" 
          variant="primary"
          disabled={loading || selectedBrands.length === 0}
          className="flex items-center space-x-2"
        >
          <span>Continue</span>
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </Button>
      </div>
    </form>
  );
};

export default BrandsStep;